// Cookie consent preferences shared by the banner and the settings page.
// Frontend-only for now - everything is stored in localStorage.

export type CookieCategory = "essential" | "functional" | "analytics" | "advertising";

export type CookiePrefs = {
  essential: boolean; // always on - strictly necessary
  functional: boolean;
  analytics: boolean;
  advertising: boolean;
};

const PREFS_KEY = "encrata_cookie_prefs";
const CONSENT_KEY = "encrata_cookie_consent";

export const CONSENT_EVENT = "encrata-cookie-consent";

export const DEFAULT_PREFS: CookiePrefs = {
  essential: true,
  functional: true,
  analytics: false,
  advertising: false,
};

const ALL_ON: CookiePrefs = { essential: true, functional: true, analytics: true, advertising: true };
const ESSENTIAL_ONLY: CookiePrefs = { essential: true, functional: false, analytics: false, advertising: false };

export function getCookiePrefs(): CookiePrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<CookiePrefs>;
    return { ...DEFAULT_PREFS, ...parsed, essential: true };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function hasConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem(CONSENT_KEY));
  } catch {
    return false;
  }
}

function persist(prefs: CookiePrefs, consent: string) {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify({ ...prefs, essential: true }));
    window.localStorage.setItem(CONSENT_KEY, consent);
    window.dispatchEvent(new Event(CONSENT_EVENT));
  } catch {
    // ignore
  }
}

// Save a specific set of preferences (from the settings page).
export function saveCookiePrefs(prefs: CookiePrefs) {
  persist({ ...prefs, essential: true }, "custom");
}

// Convenience for the banner's two buttons.
export function setConsent(choice: "accepted" | "rejected") {
  persist(choice === "accepted" ? ALL_ON : ESSENTIAL_ONLY, choice);
}
